import { Collapsible } from "@base-ui/react/collapsible";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva } from "class-variance-authority";
import { createContext, use, useEffect, useRef, useState } from "react";
import { Avatar, type UserAvatarVariant } from "../avatar";
import { Badge, type BadgeTone } from "../badge";
import { Drawer, DrawerClose, DrawerContent } from "../drawer";
import { Icon, type IconName } from "../icon";
import { IconButton } from "../icon-button";
import { Tooltip } from "../tooltip";
import { AppShellSidebarToggle } from "./app-shell";
import { useAppShellContext } from "./app-shell-context";

type SidebarContextValue = {
  collapsed: boolean;
  closeMobileNavigation?: () => void;
};

const SidebarContext = createContext<SidebarContextValue>({ collapsed: false });

/** Returns the collapsed presentation state supplied by the nearest Sidebar. */
function useSidebar() {
  return use(SidebarContext);
}

const sidebarStyles = cva(
  [
    "flex",
    "h-full",
    "min-h-0",
    "flex-col",
    "bg-surface",
    "border-r",
    "border-outline-subtle",
    "text-menu-text-default",
    "transition-[width]",
    "duration-200",
    "ease-in-out",
    "overflow-hidden",
  ],
  {
    variants: {
      collapsed: { false: null, true: null },
      inAppShell: {
        false: null,
        true: [
          "relative",
          "z-10",
          "w-(--app-shell-sidebar-width)",
          "data-[hover-expanded]:w-(--app-shell-sidebar-expanded-width)",
        ],
      },
    },
    compoundVariants: [
      {
        collapsed: false,
        inAppShell: false,
        className: "w-[260px]",
      },
      {
        collapsed: true,
        inAppShell: false,
        className: "w-[72px]",
      },
    ],
  },
);

const sidebarFooterStyles = cva(
  "sticky bottom-0 z-10 mt-auto flex shrink-0 bg-surface",
  {
    variants: {
      collapsed: {
        false: "w-full",
        true: "w-(--spacing-40) justify-center",
      },
    },
  },
);

const sidebarItemStyles = cva(
  [
    "group/sidebar-item",
    "relative",
    "inline-flex",
    "items-center",
    "border-0",
    "rounded-[var(--radius-sm)]",
    "text-left",
    "no-underline",
    "outline-none",
    "select-none",
    "cursor-pointer",
    "transition-colors",
    "duration-200",
    "ease-in-out",
    "font-[family-name:var(--font-family-label)]",
    "font-[number:var(--font-weight-medium)]",
    "text-[length:var(--font-size-label-large)]",
    "leading-[var(--font-line-height-24)]",
    "tracking-[var(--font-letter-spacing-default)]",
    "text-menu-icon-default",
    "hover:bg-menu-item-hover",
    "active:bg-menu-item-pressed",
    "focus-visible:ring-2",
    "focus-visible:ring-inset",
    "focus-visible:ring-menu-focus-ring",
    "disabled:cursor-not-allowed",
    "disabled:opacity-50",
    "disabled:pointer-events-none",
  ],
  {
    variants: {
      collapsed: {
        false: [
          "h-(--spacing-40)",
          "w-full",
          "min-w-0",
          "justify-between",
          "gap-[var(--spacing-8)]",
          "px-(--spacing-10)",
          "py-[var(--spacing-12)]",
        ],
        true: ["size-(--spacing-40)", "justify-center", "p-(--spacing-10)"],
      },
      selected: {
        false: "bg-transparent",
        true: [
          "bg-menu-item-selected",
          "text-menu-text-default",
          "hover:bg-menu-item-selected",
        ],
      },
      variant: {
        item: "",
        subItem: "",
      },
    },
    compoundVariants: [
      {
        selected: true,
        variant: "item",
        className: [
          "before:absolute",
          "before:left-[-16px]",
          "before:top-1/2",
          "before:h-(--spacing-24)",
          "before:w-[2px]",
          "before:-translate-y-1/2",
          "before:bg-primary",
          "before:rounded-r-[2px]",
          "before:content-['']",
        ],
      },
    ],
  },
);

const sidebarGroupPanelStyles = cva([
  "overflow-hidden",
  "h-(--collapsible-panel-height)",
  "[&[data-starting-style]]:h-0",
  "[&[data-ending-style]]:h-0",
  "opacity-100",
  "[&[data-starting-style]]:opacity-0",
  "[&[data-ending-style]]:opacity-0",
  "transition-[height,opacity]",
  "duration-200",
  "ease-out",
  "motion-reduce:transition-none",
]);

const sidebarSubItemDotSlotStyles =
  "flex size-(--icon-sm) shrink-0 items-center justify-center";

const sidebarCurrentIndicatorStyles = [
  "pointer-events-none",
  "absolute",
  "left-[-16px]",
  "top-1/2",
  "h-(--spacing-24)",
  "w-[2px]",
  "-translate-y-1/2",
  "rounded-r-[2px]",
  "bg-primary",
].join(" ");

export interface SidebarProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "className"> {
  /**
   * Controls the icon-only presentation for all nested sidebar primitives.
   * A collapsed sidebar expands in place while hovered.
   */
  collapsed?: boolean;
  /** Accessible label for the navigation landmark. */
  "aria-label"?: string;
  /** Leading content shown in the mobile navigation drawer header. */
  mobileHeaderLeading?: React.ReactNode;
  /** Optional actions shown beside the close control in the mobile drawer header. */
  mobileHeaderActions?: React.ReactNode;
  children: React.ReactNode;
}

function Sidebar({
  collapsed = false,
  "aria-label": ariaLabel = "Main navigation",
  children,
  onMouseEnter,
  onMouseLeave,
  mobileHeaderLeading,
  mobileHeaderActions,
  ...props
}: SidebarProps) {
  const appShell = useAppShellContext();
  const [hoverExpanded, setHoverExpanded] = useState(false);
  const pointerWithinSidebar = useRef(false);
  const suppressHoverExpansion = useRef(false);
  const shellCollapsed = appShell?.desktopSidebarCollapsed ?? collapsed;
  const previousShellCollapsed = useRef(shellCollapsed);
  const isMobile = appShell?.isMobile ?? false;
  const effectiveCollapsed = !isMobile && shellCollapsed && !hoverExpanded;

  useEffect(() => {
    if (!shellCollapsed) {
      setHoverExpanded(false);
      suppressHoverExpansion.current = false;
    } else if (
      !previousShellCollapsed.current &&
      shellCollapsed &&
      pointerWithinSidebar.current
    ) {
      suppressHoverExpansion.current = true;
      setHoverExpanded(false);
    }

    previousShellCollapsed.current = shellCollapsed;
  }, [shellCollapsed]);

  const navigation = (
    <SidebarContext
      value={{
        collapsed: effectiveCollapsed,
        closeMobileNavigation: isMobile
          ? () => appShell?.setMobileNavigationOpen(false)
          : undefined,
      }}
    >
      <nav
        {...props}
        aria-label={ariaLabel}
        data-collapsed={effectiveCollapsed ? "" : undefined}
        data-hover-expanded={hoverExpanded ? "" : undefined}
        className={`${sidebarStyles({
          collapsed: effectiveCollapsed,
          inAppShell: Boolean(appShell),
        })}${isMobile ? " w-full border-r-0" : ""}`}
        data-slot="sidebar"
        onMouseEnter={(event) => {
          onMouseEnter?.(event);
          pointerWithinSidebar.current = true;
          if (
            shellCollapsed &&
            !suppressHoverExpansion.current &&
            (!(event.relatedTarget instanceof Node) ||
              !event.currentTarget.contains(event.relatedTarget))
          ) {
            setHoverExpanded(true);
          }
        }}
        onMouseLeave={(event) => {
          onMouseLeave?.(event);
          pointerWithinSidebar.current = false;
          const wasHoverExpansionSuppressed = suppressHoverExpansion.current;
          suppressHoverExpansion.current = false;
          if (
            shellCollapsed &&
            (!(event.relatedTarget instanceof Node) ||
              !event.currentTarget.contains(event.relatedTarget))
          ) {
            if (wasHoverExpansionSuppressed) {
              setHoverExpanded(false);
              return;
            }

            setHoverExpanded(false);
          }
        }}
      >
        {appShell && !isMobile && (
          <div className="shrink-0 px-(--spacing-16) pt-(--spacing-24)">
            <AppShellSidebarToggle />
          </div>
        )}
        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
          data-slot="sidebar-navigation"
        >
          <div className="flex min-h-full flex-col gap-(--spacing-8) px-(--spacing-16) py-(--spacing-16)">
            {children}
          </div>
        </div>
      </nav>
    </SidebarContext>
  );

  if (!isMobile || !appShell) {
    return navigation;
  }

  return (
    <Drawer
      open={appShell.mobileNavigationOpen}
      onOpenChange={appShell.setMobileNavigationOpen}
      responsive={false}
      side="left"
    >
      <DrawerContent
        aria-label={ariaLabel}
        contentVariant="navigation"
        style={{ width: "75vw" }}
      >
        <div className="flex h-[68px] shrink-0 items-center justify-between gap-(--spacing-12) border-b border-outline-subtle bg-surface px-(--spacing-24)">
          <div className="min-w-0">{mobileHeaderLeading}</div>
          <div className="flex shrink-0 items-center gap-(--spacing-12)">
            {mobileHeaderActions}
            <DrawerClose
              render={
                <IconButton
                  aria-label="Close navigation"
                  icon="x-close"
                  variant="outline"
                />
              }
            />
          </div>
        </div>
        <div className="min-h-0 flex-1">{navigation}</div>
      </DrawerContent>
    </Drawer>
  );
}

export interface SidebarFooterProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "className"> {
  children: React.ReactNode;
}

function SidebarFooter({ children, ...props }: SidebarFooterProps) {
  const { collapsed } = use(SidebarContext);
  return (
    <div
      {...props}
      className={sidebarFooterStyles({ collapsed })}
      data-slot="sidebar-footer"
    >
      {children}
    </div>
  );
}

export type SidebarItemBadge = {
  count: number | string;
  tone?: BadgeTone;
  label: string;
};

/** Visual displayed before a sidebar item or group label. */
export type SidebarLeading =
  | { type: "icon"; name: IconName }
  | {
      type: "avatar";
      initials: string;
      src?: string;
      variant?: UserAvatarVariant;
    };

function SidebarLeadingVisual({ leading }: { leading?: SidebarLeading }) {
  if (!leading) return null;

  if (leading.type === "icon") {
    return (
      <Icon
        name={leading.name}
        size="sm"
        tone="inherit"
        aria-hidden
        data-slot="sidebar-item-icon"
      />
    );
  }

  return (
    <Avatar
      initials={leading.initials}
      size="xs"
      src={leading.src}
      variant={leading.variant ?? "external"}
    />
  );
}

interface SidebarItemBaseProps {
  /** Stable identifier for testing and consumer-side selection state. */
  itemId: string;
  /** Visible label in expanded mode and accessible label in collapsed mode. */
  label: string;
  /** Optional visual displayed before the label. */
  leading?: SidebarLeading;
  /** Selected/current page state. */
  selected?: boolean;
  /** Optional badge rendered after the label in expanded mode. */
  badge?: SidebarItemBadge;
  /** Collapsed tooltip content. Defaults to the label; pass false to disable it. */
  tooltip?: string | false;
  /** Visual row type. Sub-items are indented and use the dot indicator. */
  variant?: "item" | "subItem";
  /** Override the surrounding Sidebar collapsed state. */
  collapsed?: boolean;
}

export interface SidebarItemProps
  extends SidebarItemBaseProps,
    Omit<
      React.AnchorHTMLAttributes<HTMLAnchorElement>,
      "children" | "className" | "href"
    > {
  /** Link destination. One of `href` or `render` is required. */
  href?: string;
  /** Optional element used to render a router-compatible link. */
  render?: React.ReactElement;
}

type SidebarItemContentProps = Pick<
  SidebarItemBaseProps,
  "label" | "leading" | "badge" | "selected" | "variant"
> & {
  collapsed: boolean;
  trailing?: React.ReactNode;
};

function SidebarItemContent({
  label,
  leading,
  badge,
  selected = false,
  variant = "item",
  collapsed,
  trailing,
}: SidebarItemContentProps) {
  return (
    <>
      <span className="flex min-w-0 flex-1 items-center gap-[var(--spacing-8)]">
        {variant === "subItem" ? (
          <span
            aria-hidden
            className={sidebarSubItemDotSlotStyles}
            data-slot="sidebar-item-dot"
          >
            <svg
              aria-hidden
              className="relative z-[2] size-(--icon-sm)"
              viewBox="0 0 20 20"
            >
              <title>Sub-navigation item indicator</title>
              <circle
                className={
                  selected ? "fill-primary-on-container" : "fill-outline-subtle"
                }
                cx="10"
                cy="10"
                data-slot="sidebar-item-dot-indicator"
                r="2"
              />
            </svg>
          </span>
        ) : (
          <SidebarLeadingVisual leading={leading} />
        )}
        {!collapsed && (
          <span
            className="min-w-0 truncate"
            data-slot="sidebar-item-label"
            title={label}
          >
            {label}
          </span>
        )}
      </span>
      {!collapsed && badge && (
        <span className="ml-auto shrink-0" data-slot="sidebar-item-badge">
          <Badge
            count={badge.count}
            tone={badge.tone}
            aria-label={badge.label}
          />
        </span>
      )}
      {!collapsed && trailing}
      {collapsed && badge && (
        <span
          className="absolute right-(--spacing-2) top-(--spacing-2)"
          data-slot="sidebar-item-badge"
        >
          <Badge
            count={badge.count}
            tone={badge.tone}
            aria-label={badge.label}
          />
        </span>
      )}
    </>
  );
}

function SidebarItem(props: SidebarItemProps) {
  const { collapsed: contextCollapsed, closeMobileNavigation } =
    use(SidebarContext);
  const {
    itemId,
    label,
    leading,
    selected = false,
    badge,
    tooltip,
    variant = "item",
    collapsed = contextCollapsed,
    ...rest
  } = props;

  const className = sidebarItemStyles({ collapsed, selected, variant });
  const content = (
    <SidebarItemContent
      {...{ label, leading, badge, selected, variant, collapsed }}
    />
  );

  const { render, href, target, rel, onClick, ...elementAttributes } = rest;
  if (href === undefined && render === undefined) {
    throw new Error("SidebarItem requires either an href or render prop.");
  }
  const resolvedRel = target === "_blank" && !rel ? "noopener noreferrer" : rel;

  const element = useRender({
    defaultTagName: "a",
    render,
    props: mergeProps(elementAttributes as useRender.ComponentProps<"a">, {
      ...(href !== undefined && { href }),
      ...(target !== undefined && { target }),
      ...(resolvedRel !== undefined && { rel: resolvedRel }),
      onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event);
        closeMobileNavigation?.();
      },
      "data-sidebar-item": itemId,
      "aria-label": label,
      "aria-current": selected ? "page" : undefined,
      className,
      children: content,
    }),
  });

  if (!collapsed || tooltip === false) {
    return element;
  }

  return (
    <Tooltip label={tooltip ?? label} side="right" align="center">
      {element}
    </Tooltip>
  );
}

type SidebarGroupTriggerProps = Pick<
  SidebarItemBaseProps,
  "label" | "leading" | "selected" | "badge" | "tooltip"
> & {
  collapsed: boolean;
  hasSelectedChild: boolean;
  open: boolean;
} & Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    "children" | "className"
  >;

function SidebarGroupTrigger({
  label,
  leading,
  selected = false,
  badge,
  tooltip,
  collapsed,
  hasSelectedChild,
  open,
  ...props
}: SidebarGroupTriggerProps) {
  const element = (
    <button
      {...props}
      aria-label={label}
      aria-current={selected ? "page" : undefined}
      className={sidebarItemStyles({ collapsed, selected, variant: "item" })}
      type="button"
    >
      {hasSelectedChild && (
        <span
          aria-hidden
          className={sidebarCurrentIndicatorStyles}
          data-slot="sidebar-group-current-indicator"
        />
      )}
      <SidebarItemContent
        label={label}
        leading={leading}
        badge={badge}
        selected={selected}
        collapsed={collapsed}
        trailing={
          <Icon
            name={open ? "chevron-up" : "chevron-down"}
            size="xs"
            tone="secondary"
            aria-hidden
          />
        }
      />
    </button>
  );

  if (!collapsed || tooltip === false) {
    return element;
  }

  return (
    <Tooltip label={tooltip ?? label} side="right" align="center">
      {element}
    </Tooltip>
  );
}

function SidebarGroupItems({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="relative flex flex-col gap-[var(--spacing-4)] pt-[var(--spacing-4)]"
      data-slot="sidebar-group-items"
    >
      <span
        aria-hidden
        className="pointer-events-none absolute top-[24px] bottom-[20px] left-(--spacing-10) z-[1] w-(--icon-sm)"
        data-slot="sidebar-group-connector"
      >
        <svg
          aria-hidden
          className="size-full"
          preserveAspectRatio="none"
          viewBox="0 0 20 100"
        >
          <title>Sub-navigation connector</title>
          <line
            className="stroke-outline-subtle"
            vectorEffect="non-scaling-stroke"
            x1="10"
            x2="10"
            y1="0"
            y2="100"
          />
        </svg>
      </span>
      {children}
    </div>
  );
}

function SidebarStaticGroupItems({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="flex flex-col gap-[var(--spacing-4)] pt-[var(--spacing-4)]"
      data-slot="sidebar-static-group-items"
    >
      {children}
    </div>
  );
}

function SidebarStaticGroupHeader({
  label,
  leading,
  badge,
  tooltip,
  collapsed,
}: Pick<SidebarGroupProps, "label" | "leading" | "badge" | "tooltip"> & {
  collapsed: boolean;
}) {
  const element = (
    <div
      className={`${sidebarItemStyles({
        collapsed,
        selected: false,
        variant: "item",
      })} !cursor-default hover:bg-transparent active:bg-transparent`}
    >
      <SidebarItemContent
        label={label}
        leading={leading}
        badge={badge}
        collapsed={collapsed}
      />
    </div>
  );

  if (!collapsed || tooltip === false) {
    return element;
  }

  return (
    <Tooltip label={tooltip ?? label} side="right" align="center">
      {element}
    </Tooltip>
  );
}

export interface SidebarGroupProps {
  itemId: string;
  label: string;
  leading?: SidebarLeading;
  selected?: boolean;
  /** Whether a nested item is the current page. Shows the current-page indicator. */
  hasSelectedChild?: boolean;
  badge?: SidebarItemBadge;
  tooltip?: string | false;
  collapsed?: boolean;
  /** Renders an always-visible, non-interactive group header without a chevron. */
  collapsible?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

function SidebarGroup({
  itemId,
  label,
  leading,
  selected = false,
  hasSelectedChild = false,
  badge,
  tooltip,
  collapsed: collapsedProp,
  collapsible = true,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  children,
}: SidebarGroupProps) {
  const { collapsed: contextCollapsed } = use(SidebarContext);
  const collapsed = collapsedProp ?? contextCollapsed;
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;

  function handleOpenChange(nextOpen: boolean) {
    if (!isControlled) {
      setInternalOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  }

  const trigger = (
    <SidebarGroupTrigger
      label={label}
      leading={leading}
      selected={selected}
      hasSelectedChild={hasSelectedChild}
      badge={badge}
      tooltip={tooltip}
      collapsed={collapsed}
      open={open}
    />
  );

  if (!collapsible) {
    return (
      <div data-sidebar-group={itemId}>
        <SidebarStaticGroupHeader
          label={label}
          leading={leading}
          badge={badge}
          tooltip={tooltip}
          collapsed={collapsed}
        />
        <SidebarStaticGroupItems>{children}</SidebarStaticGroupItems>
      </div>
    );
  }

  return (
    <Collapsible.Root
      data-sidebar-group={itemId}
      open={open}
      onOpenChange={handleOpenChange}
    >
      <Collapsible.Trigger render={trigger} />
      <Collapsible.Panel
        className={sidebarGroupPanelStyles()}
        data-slot="sidebar-group-panel"
      >
        <SidebarGroupItems>{children}</SidebarGroupItems>
      </Collapsible.Panel>
    </Collapsible.Root>
  );
}

export { Sidebar, SidebarFooter, SidebarGroup, SidebarItem, useSidebar };
